import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';
import { Permissions } from '../../config';

import { Phone, CalendarToday, Add, AddAlert } from '@mui/icons-material';
import {
    Skeleton,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Avatar,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaFilter from 'components/SparaFilter/SparaFilter';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { fetcher, appUserApi } from '../../lib/api';
import styles from './AppUsers.module.scss';

const SKELETON_COUNT = 10;

const generateColor = (id) => {
    if (!id) return { bg: '#e2e8f0', text: '#718096' };
    const hash = String(id)
        .split('')
        .reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) % 360, 0);
    return { bg: `hsl(${hash}, 60%, 88%)`, text: `hsl(${hash}, 60%, 30%)` };
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years} ${years === 1 ? 'ár' : 'ár'} síðan`;
    if (months > 0) return `${months} ${months === 1 ? 'mánuður' : 'mánuðir'} síðan`;
    if (days > 0) return `${days} ${days === 1 ? 'dagur' : 'dagar'} síðan`;
    if (hours > 0) return `${hours} ${hours === 1 ? 'klukkustund' : 'klukkustundir'} síðan`;
    return `${minutes} ${minutes === 1 ? 'mínúta' : 'mínútur'} síðan`;
};

const getLoginMethodDisplay = (method) => {
    switch (method) {
        case 'sim':
            return { label: 'SIM', color: '#1565c0', bg: '#e3f2fd' };
        case 'app':
            return { label: 'APP', color: '#6a1b9a', bg: '#f3e5f5' };
        case 'sms':
            return { label: 'SMS', color: '#2e7d32', bg: '#e8f5e9' };
        default:
            return method ? { label: method, color: '#555', bg: '#f5f5f5' } : null;
    }
};

const UserRowSkeleton = ({ isSuperUser }) => (
    <TableRow>
        <TableCell>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Skeleton variant="circular" width={38} height={38} />
                <div>
                    <Skeleton variant="text" width={120} height={17} />
                    <Skeleton variant="text" width={160} height={14} sx={{ mt: 0.3 }} />
                </div>
            </div>
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={90} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={80} />
        </TableCell>
        {isSuperUser && (
            <TableCell>
                <Skeleton variant="rounded" width={60} height={20} />
            </TableCell>
        )}
        {isSuperUser && (
            <TableCell>
                <Skeleton variant="text" width={70} />
            </TableCell>
        )}
        {isSuperUser && (
            <TableCell>
                <Skeleton variant="rounded" width={50} height={20} />
            </TableCell>
        )}
        {isSuperUser && (
            <TableCell>
                <Skeleton variant="rounded" width={40} height={20} />
            </TableCell>
        )}
        <TableCell align="right">
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </div>
        </TableCell>
    </TableRow>
);

const AppUsers = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const store = JSON.parse(localStorage.getItem('user'));
    const userPermission = store.permission;
    const canUpdate = userPermission.includes(Permissions.APP_USER_UPDATE);
    const canDelete = userPermission.includes(Permissions.APP_USER_DELETE);
    const canCreate = userPermission.includes(Permissions.APP_USER_CREATE);
    const isSuperUser = store?.roles?.includes('super_admin');

    if (!isSuperUser) return <Navigate to="/" replace />;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setSearchPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const isSearching = debouncedSearch.length > 1;

    const swrKey = isSearching
        ? appUserApi.search(debouncedSearch, searchPage)
        : appUserApi.list(page);

    const { data, isLoading } = useSWR(swrKey, fetcher);

    useEffect(() => {
        if (!data?.results) return;
        if (isSearching ? searchPage === 1 : page === 1) {
            setAllUsers(data.results);
        } else {
            setAllUsers((prev) => {
                const existingIds = new Set(prev.map((u) => u.id));
                const newUsers = data.results.filter((u) => !existingIds.has(u.id));
                return [...prev, ...newUsers];
            });
        }
        setTotalCount(data.count || 0);
        setIsLoadingMore(false);
    }, [data]);

    const handleSearch = useCallback((e) => {
        const value = e?.target?.value ?? e ?? '';
        setSearchQuery(value);
        if (value.length === 0) {
            setPage(1);
        }
    }, []);

    const handleLoadMore = () => {
        setIsLoadingMore(true);
        if (isSearching) {
            setSearchPage((p) => p + 1);
        } else {
            setPage((p) => p + 1);
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification((prev) => ({ ...prev, open: false })), 4000);
    };

    const handleDelete = (user) => {
        setDeleteDialog({ open: true, user });
    };

    const confirmDelete = async () => {
        const { user } = deleteDialog;
        setDeleteDialog({ open: false, user: null });
        try {
            await fetcher(appUserApi.delete(user.id), 'DELETE');
            setAllUsers((prev) => prev.filter((u) => u.id !== user.id));
            setTotalCount((c) => c - 1);
            showNotification(t('common.deleted_successfully'), 'success');
        } catch {
            showNotification(t('common.error_occurred'), 'warning');
        }
    };

    const hasMore = allUsers.length < totalCount;
    const showingSkeleton = isLoading && allUsers.length === 0;

    return (
        <GridContainer>
            <ConfirmDialog
                open={deleteDialog.open}
                type="danger"
                title={t('are_you_sure')}
                message={t('delete_user_confirmation')}
                confirmText={t('delete_yes')}
                cancelText={t('cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, user: null })}
            />
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.app_users')}
                    rightComponent={
                        canCreate && (
                            <SparaButton
                                color="primary"
                                icon={Add}
                                onClick={() => navigate('/app-users/new')}
                            >
                                {t('app_user.add_new_user')}
                            </SparaButton>
                        )
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        <SparaFilter
                            filters={{}}
                            filterGroups={[]}
                            onFilterClick={() => {}}
                            onClearAll={() => handleSearch('')}
                            search={searchQuery}
                            onSearchChange={handleSearch}
                        />

                        <div className={styles.listHeader}>
                            {!showingSkeleton && (
                                <span className={styles.listHeaderCount}>
                                    {`${t('filter.showing')} ${allUsers.length} ${t(
                                        'filter.of'
                                    )} ${totalCount} ${t('menu.app_users').toLowerCase()}`}
                                </span>
                            )}
                        </div>

                        <div className={styles.tableWrap}>
                            <Table size="small" sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow className={styles.tableHead}>
                                        <TableCell>{t('user')}</TableCell>
                                        <TableCell>{t('phone')}</TableCell>
                                        <TableCell>{t('created')}</TableCell>
                                        {isSuperUser && (
                                            <TableCell>{t('app_user.last_login_method')}</TableCell>
                                        )}
                                        {isSuperUser && (
                                            <TableCell>{t('app_user.app_version')}</TableCell>
                                        )}
                                        {isSuperUser && (
                                            <TableCell>{t('app_user.device_os')}</TableCell>
                                        )}
                                        {isSuperUser && (
                                            <TableCell>{t('app_user.device_token')}</TableCell>
                                        )}
                                        <TableCell align="right">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {showingSkeleton
                                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                              <UserRowSkeleton key={i} isSuperUser={isSuperUser} />
                                          ))
                                        : allUsers.map((user) => {
                                              const { bg, text } = generateColor(user.id);
                                              return (
                                                  <TableRow
                                                      key={user.id}
                                                      className={styles.tableRow}
                                                      hover
                                                      sx={{ cursor: 'default' }}
                                                  >
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: 12,
                                                              }}
                                                          >
                                                              <Avatar
                                                                  src={user.profile_picture}
                                                                  sx={{
                                                                      width: 38,
                                                                      height: 38,
                                                                      bgcolor: bg,
                                                                      color: text,
                                                                      fontSize: '0.9rem',
                                                                      fontWeight: 600,
                                                                  }}
                                                              >
                                                                  {(
                                                                      user.name?.[0] || '?'
                                                                  ).toUpperCase()}
                                                              </Avatar>
                                                              <div>
                                                                  <p className={styles.userName}>
                                                                      {user.name || t('no_name')}
                                                                  </p>
                                                                  <p className={styles.userEmail}>
                                                                      {user.email || t('no_email')}
                                                                  </p>
                                                              </div>
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {user.phone || '—'}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {formatDate(user.created_at) || '—'}
                                                          </span>
                                                          {user.created_at && (
                                                              <span className={styles.metaSubText}>
                                                                  {timeAgo(user.created_at)}
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      {isSuperUser && (
                                                          <TableCell>
                                                              {(() => {
                                                                  const m = getLoginMethodDisplay(
                                                                      user.last_login_method
                                                                  );
                                                                  return m ? (
                                                                      <span
                                                                          className={
                                                                              styles.infoBadge
                                                                          }
                                                                          style={{
                                                                              background: m.bg,
                                                                              color: m.color,
                                                                          }}
                                                                      >
                                                                          {m.label}
                                                                      </span>
                                                                  ) : (
                                                                      <span
                                                                          className={
                                                                              styles.metaText
                                                                          }
                                                                      >
                                                                          —
                                                                      </span>
                                                                  );
                                                              })()}
                                                          </TableCell>
                                                      )}
                                                      {isSuperUser && (
                                                          <TableCell>
                                                              <span className={styles.metaText}>
                                                                  {user.app_version || '—'}
                                                              </span>
                                                          </TableCell>
                                                      )}
                                                      {isSuperUser && (
                                                          <TableCell>
                                                              {user.device_os ? (
                                                                  <span
                                                                      className={styles.infoBadge}
                                                                      style={
                                                                          user.device_os
                                                                              .toLowerCase()
                                                                              .includes('ios')
                                                                              ? {
                                                                                    background:
                                                                                        '#e3f2fd',
                                                                                    color:
                                                                                        '#1565c0',
                                                                                }
                                                                              : {
                                                                                    background:
                                                                                        '#e8f5e9',
                                                                                    color:
                                                                                        '#2e7d32',
                                                                                }
                                                                      }
                                                                  >
                                                                      {user.device_os
                                                                          .toLowerCase()
                                                                          .includes('ios')
                                                                          ? '🍎 iOS'
                                                                          : '🤖 Android'}
                                                                  </span>
                                                              ) : (
                                                                  <span className={styles.metaText}>
                                                                      —
                                                                  </span>
                                                              )}
                                                          </TableCell>
                                                      )}
                                                      {isSuperUser && (
                                                          <TableCell>
                                                              <span
                                                                  className={styles.infoBadge}
                                                                  style={
                                                                      user.has_device_token
                                                                          ? {
                                                                                background:
                                                                                    '#e8f5e9',
                                                                                color: '#2e7d32',
                                                                            }
                                                                          : {
                                                                                background:
                                                                                    '#fafafa',
                                                                                color: '#999',
                                                                                border:
                                                                                    '1px solid #e2e8f0',
                                                                            }
                                                                  }
                                                              >
                                                                  {user.has_device_token
                                                                      ? `🔔 ${t('yes')}`
                                                                      : `🔕 ${t('no')}`}
                                                              </span>
                                                          </TableCell>
                                                      )}
                                                      <TableCell
                                                          align="right"
                                                          onClick={(e) => e.stopPropagation()}
                                                      >
                                                          <div
                                                              style={{
                                                                  display: 'flex',
                                                                  justifyContent: 'flex-end',
                                                                  gap: 2,
                                                              }}
                                                          >
                                                              {canUpdate && (
                                                                  <Tooltip title={t('edit')}>
                                                                      <IconButton
                                                                          size="small"
                                                                          onClick={() =>
                                                                              navigate(
                                                                                  `/app-users/${user.id}`,
                                                                                  {
                                                                                      state: {
                                                                                          user,
                                                                                      },
                                                                                  }
                                                                              )
                                                                          }
                                                                      >
                                                                          <Edit
                                                                              sx={{ fontSize: 17 }}
                                                                          />
                                                                      </IconButton>
                                                                  </Tooltip>
                                                              )}
                                                              {canDelete && (
                                                                  <Tooltip title={t('delete')}>
                                                                      <IconButton
                                                                          size="small"
                                                                          color="error"
                                                                          onClick={() =>
                                                                              handleDelete(user)
                                                                          }
                                                                      >
                                                                          <Delete
                                                                              sx={{ fontSize: 17 }}
                                                                          />
                                                                      </IconButton>
                                                                  </Tooltip>
                                                              )}
                                                          </div>
                                                      </TableCell>
                                                  </TableRow>
                                              );
                                          })}
                                    {isLoadingMore &&
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <UserRowSkeleton
                                                key={`more-${i}`}
                                                isSuperUser={isSuperUser}
                                            />
                                        ))}
                                </TableBody>
                            </Table>
                        </div>

                        {!showingSkeleton && hasMore && (
                            <button
                                className={styles.loadMoreBtn}
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? t('common.loading_more') : t('load_more')}
                            </button>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default AppUsers;
